'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

import { Icon } from '@/components/ui/Icon';
import { useDragToScroll } from '@/hooks/useDragToScroll';
import type { CMSUniversalGrid } from '@/lib/cms/types';
import { EntryEffectWrapper } from '../EntryEffectWrapper';
import { MediaWrapper } from '../MediaWrapper';
import { UniversalCard } from '../UniversalCard';
import { spacing } from '../UniversalCard/universalSpacing';
import styles from './UniversalGrid.module.scss';

export interface UniversalGridProps extends Omit<
  CMSUniversalGrid,
  '__typename' | 'sys' | 'internalName'
> {
  className?: string;
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
}

/**
 * Universal Grid — new-generation CMS layout grid (BEC Universal components).
 * Holds Universal Cards. Supports decimal column counts (slider peek),
 * slider mode with optional scrollbar, per-device backgrounds and heights.
 */
export function UniversalGrid({
  columnDesktop,
  columnMobile,
  isSlider,
  isSliderMobile,
  hasScrollBar,
  gridHeightDesktop,
  gridHeightMobile,
  columnsGap,
  rowsGap,
  maxWidth,
  paddingTopBottom,
  paddingLeftRight,
  marginBottom,
  contentHorizontalAlignment,
  contentVerticalAlignment,
  deviceDisplay,
  backgroundColor,
  backgroundMediaDesktop,
  backgroundMediaMobile,
  entryEffectType,
  contentCollection,
  anchorId,
  className,
  cmsIndex,
}: UniversalGridProps) {
  const t = useTranslations('common');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  // Mouse swipe/drag on slider tracks (Jira: always on, regardless of hasScrollBar).
  const { isDragging, isSnapSuspended, dragHandlers } = useDragToScroll(
    scrollerRef,
    Boolean(isSlider || isSliderMobile)
  );

  const updateEnds = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    updateEnds();
    window.addEventListener('resize', updateEnds);
    return () => window.removeEventListener('resize', updateEnds);
  }, [updateEnds]);

  if (deviceDisplay === 'none') return null;

  const items = (contentCollection?.items || []).filter(
    (card) => card != null && card.deviceDisplay !== 'none'
  );
  if (items.length === 0) return null;

  const colsDesktop = Math.max(columnDesktop || 1, 0.5);
  const colsMobile = Math.max(columnMobile || 1, 0.5);
  const hAlign = (contentHorizontalAlignment || '').toLowerCase();
  const vAlign = (contentVerticalAlignment || '').toLowerCase();

  // Scroll the carousel by one card (track width + gap).
  const step = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const item = el.firstElementChild as HTMLElement | null;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    el.scrollBy({ left: dir * ((item?.offsetWidth || el.clientWidth) + gap), behavior: 'smooth' });
  };

  // Horizontal alignment of an underfilled row: shift the whole row by
  // starting the first card at a later column (cards keep their track width).
  // Not applicable on a slider breakpoint (the track scrolls instead).
  const rowStart = (cols: number, sliding?: boolean | null): number | null => {
    if (sliding || items.length >= cols) return null;
    if (hAlign === 'right') return cols - items.length + 1;
    if (hAlign === 'center') return Math.floor((cols - items.length) / 2) + 1;
    return null;
  };
  const startDesktop = rowStart(Math.max(1, Math.round(colsDesktop)), isSlider);
  const startMobile = rowStart(Math.max(1, Math.round(colsMobile)), isSliderMobile);
  const firstItemStyle =
    startDesktop || startMobile
      ? ({
          ...(startDesktop ? { '--ug-item-start-desktop': startDesktop } : {}),
          ...(startMobile ? { '--ug-item-start-mobile': startMobile } : {}),
        } as CSSProperties)
      : undefined;

  const styleVars = {
    ...(backgroundColor ? { backgroundColor } : {}),
    '--ug-cols-desktop': colsDesktop,
    '--ug-cols-desktop-int': Math.max(1, Math.round(colsDesktop)),
    '--ug-cols-desktop-ceil': Math.ceil(colsDesktop),
    '--ug-cols-mobile': colsMobile,
    '--ug-cols-mobile-int': Math.max(1, Math.round(colsMobile)),
    '--ug-cols-mobile-ceil': Math.ceil(colsMobile),
    ...(gridHeightDesktop != null ? { '--ug-height-desktop': `${gridHeightDesktop}px` } : {}),
    ...(gridHeightMobile != null ? { '--ug-height-mobile': `${gridHeightMobile}px` } : {}),
  } as CSSProperties;

  const grid = (
    <section
      id={anchorId || undefined}
      className={clsx(
        styles.section,
        spacing(paddingTopBottom) && styles[`ptb-${spacing(paddingTopBottom)}`],
        spacing(paddingLeftRight) && styles[`plr-${spacing(paddingLeftRight)}`],
        spacing(marginBottom) && styles[`mb-${spacing(marginBottom)}`],
        deviceDisplay === 'Desktop' && styles.hideMobile,
        deviceDisplay === 'Mobile' && styles.hideDesktop,
        className
      )}
      style={styleVars}
      data-testid={cmsIndex !== undefined ? `universal-grid-${cmsIndex}` : undefined}
    >
      {backgroundMediaDesktop && (
        <div
          className={clsx(styles.backgroundMedia, backgroundMediaMobile && styles.hideMobile)}
          aria-hidden="true"
        >
          <MediaWrapper media={backgroundMediaDesktop} fill disableLink sizes="100vw" />
        </div>
      )}
      {backgroundMediaMobile && (
        <div className={clsx(styles.backgroundMedia, styles.hideDesktop)} aria-hidden="true">
          <MediaWrapper media={backgroundMediaMobile} fill disableLink sizes="100vw" />
        </div>
      )}
      <div
        className={clsx(
          styles.container,
          maxWidth && maxWidth !== 'XL' && styles[`maxWidth-${maxWidth}`]
        )}
      >
        <div
          ref={scrollerRef}
          onScroll={isSlider || isSliderMobile ? updateEnds : undefined}
          {...dragHandlers}
          className={clsx(
            styles.grid,
            isSlider && styles.sliderDesktop,
            isSliderMobile && styles.sliderMobile,
            (isSlider || isSliderMobile) && hasScrollBar && styles.sliderScrollbar,
            isDragging && styles.dragging,
            isSnapSuspended && styles.snapOff,
            spacing(columnsGap) && styles[`colGap-${spacing(columnsGap)}`],
            spacing(rowsGap) && styles[`rowGap-${spacing(rowsGap)}`],
            vAlign && styles[`vAlign-${vAlign}`]
          )}
        >
          {items.map((card, index) => (
            // The same card entry may appear multiple times in one grid, so
            // sys.id alone is not unique. Device-hidden cards hide their whole
            // cell so sliders/grids don't keep blank slots.
            <div
              key={`${card.sys?.id || 'card'}-${index}`}
              className={clsx(
                styles.item,
                card.deviceDisplay === 'Desktop' && styles.hideMobile,
                card.deviceDisplay === 'Mobile' && styles.hideDesktop
              )}
              style={index === 0 ? firstItemStyle : undefined}
            >
              <UniversalCard
                {...card}
                cmsIndex={cmsIndex}
                cardIndex={index}
                gridColumnsDesktop={colsDesktop}
                gridColumnsMobile={colsMobile}
              />
            </div>
          ))}
        </div>
        {isSlider && (
          <div className={clsx(styles.arrows, styles.hideMobile)}>
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label={t('previous')}
              disabled={atStart}
              onClick={() => step(-1)}
            >
              <Icon name="arrow-left" size={20} />
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label={t('next')}
              disabled={atEnd}
              onClick={() => step(1)}
            >
              <Icon name="arrow-right" size={20} />
            </button>
          </div>
        )}
      </div>
    </section>
  );

  if (entryEffectType === 'Fade') {
    return (
      <EntryEffectWrapper enabled variant="fade">
        {grid}
      </EntryEffectWrapper>
    );
  }
  return grid;
}

UniversalGrid.displayName = 'UniversalGrid';
